import { format, parseISO, isValid, startOfDay } from 'date-fns';
import { logger } from './logger';

// URL parameter keys (short for minimal URL length)
const PARAM_EVENT_ID = 'e';
const PARAM_DATE = 'd';
const PARAM_LAT = 'lat';
const PARAM_LNG = 'lng';
const PARAM_LIST = 'list';

export interface ShareLinkData {
  eventId: number;
  date: string; // ISO format: 'yyyy-MM-dd'
  coordinates: [number, number]; // [lng, lat] for flying to location
}

export interface ParsedShareLink {
  eventId: number | null;
  date: Date | null;
  coordinates: [number, number] | null; // [lng, lat]
}

/**
 * Generate a shareable URL for an event
 * Coordinates are rounded to 6 decimal places (~0.1m accuracy)
 */
export function generateShareUrl(
  eventId: number,
  date: Date,
  coordinates: [number, number] // [lng, lat]
): string {
  const baseUrl = window.location.origin;
  const dateStr = format(date, 'yyyy-MM-dd');

  const params = new URLSearchParams();
  params.set(PARAM_EVENT_ID, eventId.toString());
  params.set(PARAM_DATE, dateStr);

  // Round to 6 decimal places for precision vs URL length
  params.set(PARAM_LAT, coordinates[1].toFixed(6));
  params.set(PARAM_LNG, coordinates[0].toFixed(6));

  return `${baseUrl}?${params.toString()}`;
}

/**
 * Parse share link parameters from current URL
 * Returns null values if params are missing or invalid
 */
export function parseShareLinkFromUrl(): ParsedShareLink {
  const params = new URLSearchParams(window.location.search);

  const eventIdStr = params.get(PARAM_EVENT_ID);
  const dateStr = params.get(PARAM_DATE);
  const latStr = params.get(PARAM_LAT);
  const lngStr = params.get(PARAM_LNG);

  let eventId: number | null = null;
  let date: Date | null = null;
  let coordinates: [number, number] | null = null;

  // Parse event ID
  if (eventIdStr) {
    const parsed = parseInt(eventIdStr, 10);
    if (!isNaN(parsed) && parsed > 0) {
      eventId = parsed;
    }
  }

  // Parse date
  if (dateStr) {
    try {
      const parsed = parseISO(dateStr);
      if (isValid(parsed)) {
        date = startOfDay(parsed);
      }
    } catch {
      // Invalid date format, leave as null
    }
  }

  // Parse coordinates
  if (latStr && lngStr) {
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);

    // Validate coordinate ranges
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      coordinates = [lng, lat]; // [lng, lat] format for MapTiler
    }
  }

  return { eventId, date, coordinates };
}

/**
 * Check if there are share link parameters in the URL
 */
export function hasShareLinkParams(): boolean {
  const params = new URLSearchParams(window.location.search);
  return params.has(PARAM_EVENT_ID);
}

/**
 * Clear share link parameters from URL without page reload
 * This keeps the URL clean after we've processed the deep link
 */
export function clearShareLinkParams(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(PARAM_EVENT_ID);
  url.searchParams.delete(PARAM_DATE);
  url.searchParams.delete(PARAM_LAT);
  url.searchParams.delete(PARAM_LNG);

  // Update URL without reload
  window.history.replaceState({}, '', url.pathname + url.search || '/');
}

/**
 * Copy text to clipboard with fallback for older browsers
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch (err) {
    logger.error('Failed to copy to clipboard:', err);
    return false;
  }
}

/**
 * Generate a shareable URL for a curated list
 */
export function generateListShareUrl(uuid: string): string {
  const baseUrl = window.location.origin;
  const params = new URLSearchParams(window.location.search);
  params.set(PARAM_LIST, uuid);
  return `${baseUrl}?${params.toString()}`;
}

/**
 * Parse list UUID from current URL
 * Returns null if param is missing or empty
 */
export function parseListFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search);
  const uuid = params.get(PARAM_LIST);
  return uuid && uuid.length > 0 ? uuid : null;
}

/**
 * Remove the list param from URL if it matches the given uuid.
 * No-op if the param is absent or belongs to a different uuid.
 */
export function clearListParamIfMatches(uuid: string): void {
  const url = new URL(window.location.href);
  if (url.searchParams.get('list') === uuid) {
    url.searchParams.delete('list');
    window.history.replaceState({}, '', url.pathname + (url.search || ''));
  }
}
